import { Injectable } from '@angular/core';
import { DataService } from '@vendure/admin-ui/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import gql from 'graphql-tag';

export interface OnboardingProgress {
    id: string;
    hasSetupStoreInfo: boolean;
    hasAddedProduct: boolean;
    hasAddedCollection: boolean;
    hasAddedProductToCollection: boolean;
    hasSetupPayments: boolean;
    hasCustomizedStore: boolean;
    hasSetupShipping: boolean;
    hasTestedPayment: boolean;
    hasLearnedEscrow: boolean;
    wizardCompleted: boolean;
    allStepsCompleted: boolean;
    completionPercentage: number;
    lastUpdated?: string;
}

const GET_ONBOARDING_PROGRESS = gql`
    query GetOnboardingProgress {
        onboardingProgress {
            id
            hasSetupStoreInfo
            hasAddedProduct
            hasAddedCollection
            hasAddedProductToCollection
            hasSetupPayments
            hasCustomizedStore
            hasSetupShipping
            hasTestedPayment
            hasLearnedEscrow
            wizardCompleted
            allStepsCompleted
            completionPercentage
            lastUpdated
        }
    }
`;

const UPDATE_ONBOARDING_STEP = gql`
    mutation UpdateOnboardingStep($step: String!, $completed: Boolean!) {
        updateOnboardingStep(step: $step, completed: $completed) {
            id
            hasSetupStoreInfo
            hasAddedProduct
            hasAddedCollection
            hasAddedProductToCollection
            hasSetupPayments
            hasCustomizedStore
            hasSetupShipping
            hasTestedPayment
            hasLearnedEscrow
            wizardCompleted
            allStepsCompleted
            completionPercentage
            lastUpdated
        }
    }
`;

const COMPLETE_WIZARD = gql`
    mutation CompleteOnboardingWizard {
        completeOnboardingWizard {
            id
            wizardCompleted
            completionPercentage
        }
    }
`;

const RESET_PROGRESS = gql`
    mutation ResetOnboardingProgress {
        resetOnboardingProgress {
            id
            completionPercentage
        }
    }
`;

@Injectable({
    providedIn: 'root',
})
export class OnboardingService {
    private progressSubject = new BehaviorSubject<OnboardingProgress | null>(null);
    public progress$ = this.progressSubject.asObservable();

    constructor(private dataService: DataService) {}

    getProgress(): Observable<OnboardingProgress> {
        return this.dataService.query(GET_ONBOARDING_PROGRESS).mapSingle((response: any) => {
            const progress = response.onboardingProgress;
            this.progressSubject.next(progress);
            return progress;
        });
    }

    updateStep(step: string, completed: boolean): Observable<OnboardingProgress> {
        return this.dataService.mutate(UPDATE_ONBOARDING_STEP, { step, completed }).pipe(
            map((response: any) => {
                const progress = response.updateOnboardingStep;
                this.progressSubject.next(progress);
                return progress;
            })
        );
    }

    completeWizard(): Observable<OnboardingProgress> {
        return this.dataService.mutate(COMPLETE_WIZARD).pipe(
            map((response: any) => {
                const progress = response.completeOnboardingWizard;
                this.progressSubject.next(progress);
                return progress;
            })
        );
    }

    resetProgress(): Observable<OnboardingProgress> {
        return this.dataService.mutate(RESET_PROGRESS).pipe(
            map((response: any) => {
                const progress = response.resetOnboardingProgress;
                this.progressSubject.next(progress);
                return progress;
            })
        );
    }

    getCurrentProgress(): OnboardingProgress | null {
        return this.progressSubject.value;
    }
}
